import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import ScrollReveal from '../common/ScrollReveal';

type FaqItem = { q: string; a: string };

function formatTeacherNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} و ${names[1]}`;
  return `${names.slice(0, -1).join('، ')}، و ${names[names.length - 1]}`;
}

const HomeFaq: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [teacherNames, setTeacherNames] = useState<string[]>([]);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get<{
          success: boolean;
          data?: Array<{ fullName: string; active: boolean }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data?.data) {
          setTeacherNames(
            response.data.data
              .filter((t) => t.active && t.fullName?.trim())
              .map((t) => t.fullName.trim())
          );
        }
      } catch (error) {
        console.error('Error fetching teachers for FAQ:', error);
      }
    };

    fetchTeachers();
  }, []);

  const faqs: FaqItem[] = useMemo(() => {
    const namesText = formatTeacherNames(teacherNames);
    const teachersAnswer = namesText
      ? `المدرسين الموجودين حالياً على المنصة: ${namesText}. تقدر تشوف كورسات كل مدرس من قسم المدرسين في الصفحة الرئيسية.`
      : 'تقدر تشوف قائمة المدرسين وكورساتهم من قسم المدرسين في الصفحة الرئيسية.';

    return [
      {
        q: 'مين المدرسين اللي موجودين على المنصة؟',
        a: teachersAnswer,
      },
      {
        q: 'المنصة مجانية ولا مدفوعة؟',
        a: 'التسجيل على المنصة مجاني، والكورسات بعضها مجاني وبعضها باشتراك مدفوع — والسعر بيظهر على صفحة كل كورس قبل ما تشترك.',
      },
      {
        q: 'لو نسيت كلمة السر أعمل إيه؟',
        a: 'من صفحة تسجيل الدخول اختار «نسيت كلمة السر»، أدخل رقم هاتفك أو بريدك، وهتوصلك خطوات إعادة تعيين كلمة المرور.',
      },
      {
        q: 'إزاي أتواصل مع الدعم الفني؟',
        a: 'تقدر تستخدم قسم المنتدى داخل بوابة الطالب، أو راسلنا عبر البريد الإلكتروني — وفريق الدعم بيرد في أقرب وقت خلال ساعات العمل.',
      },
    ];
  }, [teacherNames]);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const content = (
    <>
      <ScrollReveal variant="up" className="mb-8 max-w-2xl md:mb-10">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="inline-block h-5 w-1 shrink-0 rounded-full bg-[#C09048]" aria-hidden />
          <p className="text-sm font-semibold text-[#A87824]">مساعدة سريعة</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#C09048] text-white">
            <HelpCircle className="h-5 w-5" aria-hidden />
          </span>
          <h2 className="text-2xl font-bold text-[#001848] font-cairo-bold sm:text-3xl lg:text-4xl">
            أسئلة شائعة
          </h2>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-[#5A6B82] sm:text-base">
          إجابات مختصرة لأكثر الأسئلة اللي بيسألها الطلاب وأولياء الأمور.
        </p>
      </ScrollReveal>

      <ul className="mx-auto flex max-w-3xl flex-col gap-3 sm:gap-4">
        {faqs.map((item, index) => {
          const isOpen = openIndex === index;
          const num = String(index + 1).padStart(2, '0');
          const panelId = `faq-panel-${index}`;
          const buttonId = `faq-button-${index}`;

          return (
            <ScrollReveal key={item.q} variant="up" delay={index * 60}>
              <li
                className={`overflow-hidden rounded-2xl border transition-all duration-200 ${
                  isOpen
                    ? 'border-[#C09048]/50 bg-[#FAF7F0] shadow-md shadow-[#001848]/8'
                    : 'border-[#E5DCCF] bg-[#FAF7F0]/80 hover:border-[#C09048]/40'
                }`}
              >
                <button
                  id={buttonId}
                  type="button"
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="flex min-h-12 w-full items-center gap-3 p-3.5 text-right sm:gap-4 sm:p-4"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold tabular-nums transition-colors ${
                      isOpen
                        ? 'bg-[#C09048] text-white'
                        : 'border border-[#E5DCCF] bg-[#EFE8DC] text-[#001848]'
                    }`}
                  >
                    {num}
                  </span>
                  <span
                    className={`min-w-0 flex-1 text-sm font-semibold leading-relaxed sm:text-base ${
                      isOpen ? 'text-[#001848]' : 'text-[#0C2448]'
                    }`}
                  >
                    {item.q}
                  </span>
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
                      isOpen
                        ? 'rotate-180 border-[#E0C99A] bg-[#F8F1E4] text-[#A87824]'
                        : 'border-[#E5DCCF] bg-white/80 text-[#001848]'
                    }`}
                    aria-hidden
                  >
                    <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                </button>

                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="border-t border-[#E5DCCF] px-3.5 pb-4 pe-3.5 ps-[3.75rem] sm:px-4 sm:pb-5 sm:ps-[4.25rem]">
                      <div className="mb-3 h-0.5 w-10 bg-[#C09048]" />
                      <p className="text-sm leading-relaxed text-[#5A6B82] sm:text-base sm:leading-7">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            </ScrollReveal>
          );
        })}
      </ul>
    </>
  );

  if (embedded) {
    return <div id="faq-section">{content}</div>;
  }

  return (
    <section id="faq-section" className="relative overflow-hidden py-10 sm:py-12 lg:py-16">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{content}</div>
    </section>
  );
};

export default HomeFaq;
